import './style.css';
import { computeQimen, type QimenResult } from './engine/qimen';
import stemCombos from './data/stem_combos.json';
import {
  searchCuiyun,
  formatBoardTime,
  summarizeHits,
  normalizeCrossLayer,
  type CuiyunBoard,
  type CuiyunSearchResult,
  type RelationFilter,
  type LayerItemFilters,
  type CrossLayerRel,
  type CrossLayerSide,
  type CrossLayerFilter,
} from './lib/cuiyun';
import {
  type CuiyunLayer,
  LAYER_LABEL,
  LAYER_ITEMS,
  layerItemLabel,
  normalizeLayerItem,
} from './lib/wuxing';

const app = document.querySelector<HTMLDivElement>('#app')!;

const now = new Date();
let state = {
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  day: now.getDate(),
  hour: now.getHours(),
  minute: now.getMinutes(),
  clockwise: 0,
  antiClockwise: 0,
};

let statusMsg = '';
let chartLoaded = false;

function defaultCuiRange() {
  const from = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: 0,
    minute: 0,
  };
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59);
  const to = {
    year: end.getFullYear(),
    month: end.getMonth() + 1,
    day: end.getDate(),
    hour: 23,
    minute: 59,
  };
  return { from, to };
}
const _cuiInit = defaultCuiRange();
let cuiFrom = { ..._cuiInit.from };
let cuiTo = { ..._cuiInit.to };
let cuiLayers: Set<CuiyunLayer> = new Set(['gan', 'men', 'xing', 'shen']);
let cuiLayerItems: Record<CuiyunLayer, Set<string>> = {
  gan: new Set(),
  men: new Set(),
  xing: new Set(),
  shen: new Set(),
};
let cuiRelation: RelationFilter = null;
let cuiResult: CuiyunSearchResult | null = null;
let cuiBusy = false;
let cuiFiltersExpanded = true;
let cuiCrossA: CrossLayerSide | null = null;
let cuiCrossRel: CrossLayerRel | null = null;
let cuiCrossB: CrossLayerSide | null = null;
let cuiCrossSamePalace = false;
let cuiCrossALayer: CuiyunLayer = 'gan';
let cuiCrossBLayer: CuiyunLayer = 'xing';
let cuiHighlight: RelationFilter = null;
let cuiHighlightPalaces: Set<number> = new Set();
let cuiCrossHighlight = false;

const SLOT_TONE = ['blue', 'yellow', 'blue', 'yellow', 'yellow', 'yellow', 'blue', 'yellow', 'blue'] as const;
const COMBO_MAP = stemCombos as Record<string, string>;

function numInput(id: string, label: string, value: number, min: number, max: number): string {
  return `<label>${label}<input id="${id}" type="number" min="${min}" max="${max}" value="${value}" /></label>`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function starLabel(s: string): string {
  return s === '禽' ? '芮' : s;
}

function lookupCombo(tian: string, di: string): string | null {
  if (!tian || !di) return null;
  const key = tian + di;
  const text = COMBO_MAP[key];
  return text && !key.startsWith('_') ? text : null;
}

function plateCombos(result: QimenResult): { palace: number; name: string; pair: string; text: string }[] {
  const out: { palace: number; name: string; pair: string; text: string }[] = [];
  const seen = new Set<string>();
  for (const p of result.palaces) {
    if (p.luoshu === 5) continue;
    const tianStems = [p.tianGan, p.jiXianTian].filter(Boolean);
    const diStems = [p.diGan, p.jiXianDi].filter(Boolean);
    for (const t of tianStems) {
      for (const d of diStems) {
        const text = lookupCombo(t, d);
        if (!text) continue;
        const pair = `${t}+${d}`;
        const id = `${p.luoshu}:${pair}`;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ palace: p.luoshu, name: p.name, pair, text });
      }
    }
  }
  return out;
}

function renderCenterPalace(result: QimenResult, luoshu: number, slotIndex: number): string {
  const p = result.palaces.find((x) => x.luoshu === luoshu)!;
  const tone = SLOT_TONE[slotIndex] ?? 'yellow';
  const juShort = `${result.dun === '阳遁' ? '阳' : '阴'}${result.juLabel}`;
  const xunLine = `${result.xunshou}${result.xunshouStem}旬`;
  const hourLine = `${result.pillars.hour}時`;

  return `<div class="palace center tone-${tone}" data-luoshu="${luoshu}">
    <div class="p-ov"></div>
    <div class="c-meta">
      <div class="c-ju">${esc(juShort)}</div>
      <div class="c-xun">${esc(xunLine)}</div>
      <div class="c-hour">${esc(hourLine)}</div>
    </div>
    <div class="c-zf">
      <span class="c-lbl">值符</span><span class="c-val">${esc(starLabel(result.zhifu.star))}</span>
      <span class="c-lbl">值使</span><span class="c-val">${esc(result.zhishi.door)}</span>
    </div>
    <div class="c-stems" title="中宮干星">
      <span class="di-gan">${esc(p.diGan || '·')}</span>
      <span class="tian-xing">${esc(starLabel(p.tianPanXing || p.star || '') || '·')}</span>
      <span class="tian-gan">${esc(p.tianGan || '·')}</span>
    </div>
  </div>`;
}

function renderPalaceCell(result: QimenResult, luoshu: number, slotIndex: number): string {
  if (luoshu === 5) return renderCenterPalace(result, luoshu, slotIndex);

  const p = result.palaces.find((x) => x.luoshu === luoshu)!;
  const isZhifu = result.zhifu.palace === luoshu;
  const isZhishi = result.zhishi.palace === luoshu;
  const tone = SLOT_TONE[slotIndex] ?? 'blue';
  const marks = isZhishi ? '使' : '';
  const tianXing = starLabel(p.tianPanXing || p.star || '');
  const diXing = starLabel(p.diPanXing || '');

  const shenParts = [p.shen, p.diShen].filter(Boolean);
  const shenHtml = shenParts.length
    ? shenParts.map((s) => `<span class="shen">${esc(s)}</span>`).join('')
    : '<span class="shen muted">·</span>';

  const tianGans = [p.tianGan, p.jiXianTian].filter(Boolean);
  const tianGanHtml = tianGans.length
    ? tianGans.map((g) => `<span class="tian-gan">${esc(g)}</span>`).join('')
    : '<span class="tian-gan">·</span>';

  const diGanHtml = p.diGan
    ? `<span class="di-gan">${esc(p.diGan)}</span>${
        p.jiXianDi ? `<span class="jx" title="寄顯（地）">${esc(p.jiXianDi)}</span>` : ''
      }`
    : '<span class="di-gan">·</span>';

  const yinKun = result.palaces.find((x) => x.luoshu === 2)?.yinGan ?? '';
  const yinZhong = result.palaces.find((x) => x.luoshu === 5)?.yinGan ?? '';
  const kunDi = result.palaces.find((x) => x.luoshu === 2)?.diGan ?? '';
  const zhongDi = result.palaces.find((x) => x.luoshu === 5)?.diGan ?? '';
  let yinExtra = '';
  if (p.yinGan && result.xunshouInZhong && yinKun && p.yinGan === yinKun) {
    yinExtra = yinZhong;
  } else if (p.yinGan && !result.xunshouInZhong && kunDi && p.yinGan === kunDi) {
    yinExtra = zhongDi;
  }
  const yinList = [p.yinGan, yinExtra].filter(Boolean);
  const yinHtml = yinList.length
    ? yinList.map((g) => `<span class="yin-gan">${esc(g)}</span>`).join('')
    : '';

  const tags = [
    p.maXing ? '<span class="mk ma">馬</span>' : '',
    p.po ? '<span class="mk po">迫</span>' : '',
    p.mu ? '<span class="mk mu">墓</span>' : '',
    p.xing ? '<span class="mk xing">刑</span>' : '',
    p.kongWang ? '<span class="mk kong">空</span>' : '',
  ]
    .filter(Boolean)
    .join('');

  const hitSheng = cuiHighlight === '生我' && cuiHighlightPalaces.has(luoshu);
  const hitKe = cuiHighlight === '我剋' && cuiHighlightPalaces.has(luoshu);
  const hitBen = cuiHighlight === '本宮' && cuiHighlightPalaces.has(luoshu);
  const hitCross = cuiCrossHighlight && cuiHighlightPalaces.has(luoshu);
  const hitCls = hitCross
    ? ' hit-cross'
    : hitBen
      ? ' hit-ben'
      : hitSheng
        ? ' hit-sheng'
        : hitKe
          ? ' hit-ke'
          : '';

  return `<div class="palace tone-${tone}${isZhifu ? ' is-zhifu' : ''}${isZhishi ? ' is-zhishi' : ''}${hitCls}" data-luoshu="${luoshu}">
<div class="p-ov"></div>
    <div class="p-corner">
      ${marks ? `<span class="p-mark">${esc(marks)}</span>` : ''}
      <span class="di-xing" title="地盤星">${esc(diXing || '')}</span>
      <span class="p-luo">${p.luoshu}</span>
    </div>
    <div class="p-shen" title="神">${shenHtml}</div>
    <div class="p-body">
      <div class="p-yin" title="引干">${yinHtml}</div>
      <div class="p-core">
        <span class="tian-xing" title="天盤星">${esc(tianXing || '·')}</span>
        <span class="door" title="門">${esc(p.door || '·')}</span>
      </div>
      <div class="p-stems">
        <div class="tian-gan-row" title="天盤天干">${tianGanHtml}</div>
        <div class="di-gan-row" title="地盤天干">${diGanHtml}</div>
      </div>
    </div>
    <div class="p-tags">${tags || '<span class="mk muted"> </span>'}</div>
    <div class="p-name">${esc(p.name)}</div>
  </div>`;
}

function readCuiFromDom(): void {
  const n = (id: string, fallback: number) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return fallback;
    const v = Number(el.value);
    return Number.isFinite(v) ? v : fallback;
  };
  cuiFrom = {
    year: n('cui-from-year', cuiFrom.year),
    month: n('cui-from-month', cuiFrom.month),
    day: n('cui-from-day', cuiFrom.day),
    hour: n('cui-from-hour', cuiFrom.hour),
    minute: n('cui-from-minute', cuiFrom.minute),
  };
  cuiTo = {
    year: n('cui-to-year', cuiTo.year),
    month: n('cui-to-month', cuiTo.month),
    day: n('cui-to-day', cuiTo.day),
    hour: n('cui-to-hour', cuiTo.hour),
    minute: n('cui-to-minute', cuiTo.minute),
  };
}

function currentCrossLayerFilter(): CrossLayerFilter | null {
  if (!cuiCrossA || !cuiCrossB || !cuiCrossRel) return null;
  return normalizeCrossLayer({
    a: cuiCrossA,
    relation: cuiCrossRel,
    b: cuiCrossB,
    samePalace: cuiCrossSamePalace,
  });
}

function crossLayerHint(): string {
  const f = currentCrossLayerFilter();
  if (!f) {
    if (cuiCrossRel || cuiCrossA || cuiCrossB) {
      return '跨層生剋未齊（須選 A、生/剋、B）';
    }
    return '跨層生剋關';
  }
  const aLab = layerItemLabel(f.a.layer, f.a.id);
  const bLab = layerItemLabel(f.b.layer, f.b.id);
  return `${aLab}宮洛書${f.relation}${bLab}宮洛書${f.samePalace ? ' · 須同宮' : ' · 異宮可'}`;
}

function runCuiyunSearch(): void {
  readCuiFromDom();
  cuiBusy = true;
  statusMsg = '正在搜尋可催運盤…';
  render();
  try {
    const layerItems: LayerItemFilters = {};
    for (const layer of ['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]) {
      if (cuiLayerItems[layer].size) {
        layerItems[layer] = [...cuiLayerItems[layer]];
      }
    }
    cuiResult = searchCuiyun({
      from: { ...cuiFrom },
      to: { ...cuiTo },
      layers: [...cuiLayers],
      layerItems,
      relation: cuiRelation,
      crossLayer: currentCrossLayerFilter(),
      maxSlots: 450,
    });
    const n = cuiResult.boards.length;
    statusMsg = `可催運盤：找到 ${n} 盤（掃描 ${cuiResult.scanned} 時辰；排除伏／反吟 ${cuiResult.excludedFuyin}、本宮凶 ${cuiResult.excludedDayPalace}、生我宮凶 ${cuiResult.excludedShengWoBad}、庚丙 ${cuiResult.excludedGengBing}${cuiResult.truncated ? '；已截斷' : ''}）`;
  } catch (err) {
    console.error(err);
    cuiResult = null;
    statusMsg = `搜尋失敗：${err instanceof Error ? err.message : String(err)}`;
  } finally {
    cuiBusy = false;
    render();
  }
}

function loadCuiyunBoard(board: CuiyunBoard): void {
  state = {
    ...state,
    year: board.year,
    month: board.month,
    day: board.day,
    hour: board.hour,
    minute: board.minute,
  };
  chartLoaded = true;
  cuiCrossHighlight = false;
  if (board.crossLayerHit && currentCrossLayerFilter()) {
    cuiCrossHighlight = true;
    cuiHighlight = null;
    cuiHighlightPalaces = new Set(board.crossLayerHit.highlightPalaces);
  } else if (cuiRelation === '本宮') {
    cuiHighlight = '本宮';
    cuiHighlightPalaces = new Set(
      board.dayStemPalace != null ? [board.dayStemPalace] : [],
    );
  } else if (cuiRelation === '我剋') {
    cuiHighlight = '我剋';
    cuiHighlightPalaces = new Set(board.woKe.map((h) => h.luoshu));
  } else if (cuiRelation === '生我') {
    cuiHighlight = '生我';
    cuiHighlightPalaces = new Set(board.shengWo.map((h) => h.luoshu));
  } else {
    cuiHighlight = '生我';
    cuiHighlightPalaces = new Set(board.shengWo.map((h) => h.luoshu));
  }
  const hlLabel = cuiCrossHighlight
    ? `跨層${board.crossLayerHit?.label ?? ''}`
    : cuiRelation === '本宮'
      ? '本宮'
      : cuiRelation === '我剋'
        ? '我剋'
        : cuiRelation === '生我'
          ? '生我'
          : '生我';
  const crossNote = board.crossLayerHit
    ? `；跨層${board.crossLayerHit.label} @ ${board.crossLayerHit.highlightPalaces.join(',')}`
    : '';
  statusMsg = `已載入可催運盤 ${formatBoardTime(board)}；日干${board.dayStem}（${board.dayWx}）落${board.dayStemPalaceName}${board.dayStemPalace ?? ''}（宮${board.meWx}）；高亮「${hlLabel}」宮${crossNote}`;
  render();
}

function renderCrossLayerFilter(): string {
  const layerTabs = (side: 'a' | 'b', cur: CuiyunLayer) =>
    (['gan', 'men', 'xing', 'shen'] as CuiyunLayer[])
      .map((l) => {
        const on = cur === l;
        return `<button type="button" data-act="cui-cross-layer" data-side="${side}" data-layer="${l}" class="cui-item ${on ? 'active' : ''}">${LAYER_LABEL[l]}</button>`;
      })
      .join('');

  const itemChips = (side: 'a' | 'b', layer: CuiyunLayer, selected: CrossLayerSide | null) =>
    LAYER_ITEMS[layer]
      .map((o) => {
        const on = selected?.layer === layer && selected.id === o.id;
        return `<button type="button" data-act="cui-cross-item" data-side="${side}" data-layer="${layer}" data-item="${o.id}" class="cui-item ${on ? 'active' : ''}">${esc(o.label)}</button>`;
      })
      .join('');

  const relBtn = (rel: CrossLayerRel | null, label: string) => {
    const on = cuiCrossRel === rel;
    return `<button type="button" data-act="cui-cross-rel" data-rel="${rel ?? ''}" class="${on ? 'active' : ''}">${label}</button>`;
  };

  const aSel = cuiCrossA ? layerItemLabel(cuiCrossA.layer, cuiCrossA.id) : '—';
  const bSel = cuiCrossB ? layerItemLabel(cuiCrossB.layer, cuiCrossB.id) : '—';
  const relSel = cuiCrossRel ?? '關';

  return `<div class="cui-cross-block">
    <div class="cui-cross-title">跨層生剋 <span class="notes">（洛書宮位五行：A所在宮 ${esc(relSel)} B所在宮；非固有五行）</span></div>
    <div class="cui-cross-side">
      <span class="cui-item-label">A</span>
      ${layerTabs('a', cuiCrossALayer)}
      <button type="button" data-act="cui-cross-clear" data-side="a" class="cui-item cui-item-any ${!cuiCrossA ? 'active' : ''}">關</button>
      ${itemChips('a', cuiCrossALayer, cuiCrossA)}
    </div>
    <div class="btn-row cui-cross-rel">
      <span class="notes">關係</span>
      ${relBtn('生', '生')}
      ${relBtn('剋', '剋')}
      ${relBtn(null, '關')}
      <label class="cui-same-palace"><input type="checkbox" data-act="cui-cross-same" ${cuiCrossSamePalace ? 'checked' : ''}/> 須同宮</label>
    </div>
    <div class="cui-cross-side">
      <span class="cui-item-label">B</span>
      ${layerTabs('b', cuiCrossBLayer)}
      <button type="button" data-act="cui-cross-clear" data-side="b" class="cui-item cui-item-any ${!cuiCrossB ? 'active' : ''}">關</button>
      ${itemChips('b', cuiCrossBLayer, cuiCrossB)}
    </div>
    <p class="notes cui-cross-help">選定例：「乙剋芮」＝天干乙＋剋＋星芮 → 乙落宮洛書剋飛布芮落宮洛書；「合生乙」＝神合＋生＋天干乙。中宮排除。目前：${esc(aSel)} ${esc(relSel)} ${esc(bSel)}${cuiCrossSamePalace ? '（須同宮）' : ''}。</p>
  </div>`;
}

function renderCuiyunPanel(): string {
  const layerBtn = (id: CuiyunLayer, label: string) => {
    const on = cuiLayers.has(id);
    return `<button type="button" data-act="cui-layer" data-layer="${id}" class="${on ? 'active' : ''}">${label}</button>`;
  };
  const relBtn = (rel: RelationFilter, label: string) => {
    const on = cuiRelation === rel;
    return `<button type="button" data-act="cui-rel" data-rel="${rel ?? ''}" class="${on ? 'active' : ''}">${label}</button>`;
  };

  const itemPickerRow = (layer: CuiyunLayer) => {
    if (!cuiLayers.has(layer)) return '';
    const opts = LAYER_ITEMS[layer];
    const selected = cuiLayerItems[layer];
    const chips = opts
      .map((o) => {
        const on = selected.has(o.id);
        return `<button type="button" data-act="cui-item" data-layer="${layer}" data-item="${o.id}" class="cui-item ${on ? 'active' : ''}">${esc(o.label)}</button>`;
      })
      .join('');
    const anyOn = selected.size === 0;
    return `<div class="cui-item-row">
      <span class="cui-item-label">${LAYER_LABEL[layer]}</span>
      <button type="button" data-act="cui-item-any" data-layer="${layer}" class="cui-item cui-item-any ${anyOn ? 'active' : ''}">不限</button>
      ${chips}
    </div>`;
  };

  let listHtml = '';
  if (cuiResult) {
    if (!cuiResult.boards.length) {
      listHtml = `<p class="notes">此區間無通過篩選之盤。可放寬細項／「生我／我剋／本宮」或檢查日時範圍。</p>`;
    } else {
      const rows = cuiResult.boards
        .map((b, idx) => {
          const sw = summarizeHits(b.shengWo);
          const wk = summarizeHits(b.woKe);
          const bg = b.benGongHit ? summarizeHits([b.benGongHit]) : '—';
          const bgLine =
            cuiRelation === '本宮' || b.benGongHit
              ? `<div class="cui-sub notes"><span class="cui-tag ben">本宮</span> ${esc(bg)}</div>`
              : '';
          const crossLine = b.crossLayerHit
            ? `<div class="cui-sub notes"><span class="cui-tag cross">跨層</span> ${esc(b.crossLayerHit.label)}</div>`
            : '';
          return `<div class="cui-row" data-cui-idx="${idx}">
            <div class="cui-main">
              <div class="cui-title">${esc(formatBoardTime(b))} · ${esc(b.pillars.year)} ${esc(b.pillars.month)} ${esc(b.pillars.day)} ${esc(b.pillars.hour)}</div>
              <div class="cui-sub notes">日干${esc(b.dayStem)}（${esc(b.dayWx)}）落${esc(b.dayStemPalaceName)}${b.dayStemPalace ?? ''} · ${esc(b.result.dun)}${esc(b.result.juLabel)}</div>
              ${bgLine}
              ${crossLine}
              <div class="cui-sub notes"><span class="cui-tag sheng">生我</span> ${esc(sw)}</div>
              <div class="cui-sub notes"><span class="cui-tag ke">我剋</span> ${esc(wk)}</div>
            </div>
            <div class="cui-btns">
              <button type="button" data-act="cui-load" data-cui-idx="${idx}">載入盤</button>
            </div>
          </div>`;
        })
        .join('');
      listHtml = `<div class="cui-list">${rows}</div>`;
    }
  } else {
    listHtml = `<p class="notes">設定日時區間後按「搜尋」。硬性排除：八門／天干伏吟或反吟；日干本宮有空／刑／墓／迫；生我宮有空／刑／墓／迫；生我宮或日干本宮有庚＋丙／丙＋庚。跨層生剋＝符號 A＋生/剋＋符號 B，比對兩符號<strong>所在宮洛書五行</strong>（不含中宮）。</p>`;
  }

  const layerParts: string[] = [];
  for (const l of ['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]) {
    if (!cuiLayers.has(l)) continue;
    const sel = [...cuiLayerItems[l]];
    if (!sel.length) layerParts.push(`${LAYER_LABEL[l]}（不限）`);
    else layerParts.push(`${LAYER_LABEL[l]}：${sel.map((id) => layerItemLabel(l, id)).join('')}`);
  }
  const layerHint = layerParts.join(' · ') || '（未選層）';
  const relHint =
    cuiRelation === '本宮'
      ? '僅顯示日干本宮命中細項者'
      : cuiRelation
        ? `僅顯示有「${cuiRelation}」命中者`
        : '未限生剋／本宮（顯示全部可催盤）';
  const crossHint = crossLayerHint();

  return `
    <div class="cuiyun-panel is-open">
      <div class="cuiyun-header">
        <div class="cuiyun-title">催運搜尋</div>
        ${statusMsg ? `<span class="status-msg">${esc(statusMsg)}</span>` : ''}
      </div>
      <div class="cuiyun-body">
      <div class="cui-range controls">
        <span class="cui-range-label">起</span>
        ${numInput('cui-from-year', '年', cuiFrom.year, 1900, 2100)}
        ${numInput('cui-from-month', '月', cuiFrom.month, 1, 12)}
        ${numInput('cui-from-day', '日', cuiFrom.day, 1, 31)}
        ${numInput('cui-from-hour', '時', cuiFrom.hour, 0, 23)}
        ${numInput('cui-from-minute', '分', cuiFrom.minute, 0, 59)}
        <span class="cui-range-label">迄</span>
        ${numInput('cui-to-year', '年', cuiTo.year, 1900, 2100)}
        ${numInput('cui-to-month', '月', cuiTo.month, 1, 12)}
        ${numInput('cui-to-day', '日', cuiTo.day, 1, 31)}
        ${numInput('cui-to-hour', '時', cuiTo.hour, 0, 23)}
        ${numInput('cui-to-minute', '分', cuiTo.minute, 0, 59)}
        <button type="button" data-act="cui-search" class="btn-primary" ${cuiBusy ? 'disabled' : ''}>${cuiBusy ? '搜尋中…' : '搜尋'}</button>
      </div>
      <div class="cui-filter-toggle-row">
        <button type="button" data-act="cui-toggle-filters" class="cui-filter-toggle" aria-expanded="${cuiFiltersExpanded ? 'true' : 'false'}">${cuiFiltersExpanded ? '收起篩選' : '展開篩選'}</button>
        ${cuiFiltersExpanded ? '' : `<span class="notes cui-filter-summary">目前：${esc(layerHint)} · ${esc(relHint)} · ${esc(crossHint)}</span>`}
      </div>
      ${
        cuiFiltersExpanded
          ? `<div class="cui-filters-block">
      <div class="btn-row cui-filters">
        <span class="notes">層</span>
        ${layerBtn('gan', '天干')}
        ${layerBtn('men', '門')}
        ${layerBtn('xing', '星')}
        ${layerBtn('shen', '神')}
        <span class="notes" style="margin-left:0.5rem">日干落宮</span>
        ${relBtn('生我', '生我')}
        ${relBtn('我剋', '我剋')}
        ${relBtn(null, '不限')}
        ${relBtn('本宮', '本宮')}
      </div>
      <div class="cui-item-pickers">
        ${itemPickerRow('gan')}
        ${itemPickerRow('men')}
        ${itemPickerRow('xing')}
        ${itemPickerRow('shen')}
      </div>
      ${renderCrossLayerFilter()}
      <p class="notes cui-hint">目前：${esc(layerHint)} · ${esc(relHint)} · ${esc(crossHint)}。跨層生剋比對符號<strong>所在宮洛書五行</strong>。硬性排除不變。</p>
      </div>`
          : ''
      }
      ${listHtml}
      </div>
    </div>`;
}

function renderComboPanel(result: QimenResult): string {
  const rows = plateCombos(result);
  if (!rows.length) {
    return `<div class="combo-panel"><strong>天干組合</strong><p class="notes">盤面無對應古文組合。</p></div>`;
  }
  const list = rows
    .map(
      (r) =>
        `<div class="combo-row"><span class="combo-key">${r.palace}${esc(r.name)} ${esc(r.pair)}</span><span class="combo-text">${esc(r.text)}</span></div>`,
    )
    .join('');
  return `<div class="combo-panel"><strong>天干組合</strong>（盤面天盤干×地盤干，古文）<div class="combo-list">${list}</div></div>`;
}

function renderChart(): string {
  if (!chartLoaded) {
    return `<div class="panel"><p class="notes">搜尋後按「載入盤」會喺呢度顯示九宮預覽同高亮。</p></div>`;
  }
  const result = computeQimen(state);
  const order = result.displayOrder;
  const kongNames = result.kongWangPalaces
    .map((n) => result.palaces.find((p) => p.luoshu === n)?.name ?? String(n))
    .join('');
  const maName =
    result.maXingPalace != null
      ? (result.palaces.find((p) => p.luoshu === result.maXingPalace)?.name ?? '')
      : '—';
  const fuyinBits = [result.menFuyin, result.ganFuyin].filter(Boolean).join(' · ') || '無';

  return `
    <div class="panel meta">
      <div class="meta-pillars">四柱
        <span><strong>${result.pillars.year}</strong>年</span>
        <span><strong>${result.pillars.month}</strong>月</span>
        <span><strong>${result.pillars.day}</strong>日</span>
        <span><strong>${result.pillars.hour}</strong>時</span>
      </div>
      <div>節氣 <strong>${result.jieqi.name}</strong></div>
      <div>遁 <strong>${result.dun}</strong> · <strong>${result.juLabel}</strong></div>
      <div>旬首 <strong>${result.xunshou}</strong>（${result.xunshouStem}）</div>
      <div>值符 <strong>${starLabel(result.zhifu.star)}</strong> @ ${result.zhifu.palaceName}</div>
      <div>值使 <strong>${result.zhishi.door}</strong> @ ${result.zhishi.palaceName}</div>
      <div>空亡 <strong>${kongNames || '—'}</strong></div>
      <div>馬星 <strong>${maName}</strong></div>
      <div>伏吟／反吟 <strong>${fuyinBits}</strong></div>
    </div>
    <div class="panel grid-wrap">
      <div>
        <div class="palace-grid">
          ${order.map((n, i) => renderPalaceCell(result, n, i)).join('')}
        </div>
        <p class="legend notes">高亮：生我綠／我剋紅／本宮藍／跨層橙。宮格佈局同奇門排盤一致。</p>
        ${renderComboPanel(result)}
      </div>
    </div>`;
}

function bind(): void {
  for (const id of [
    'cui-from-year', 'cui-from-month', 'cui-from-day', 'cui-from-hour', 'cui-from-minute',
    'cui-to-year', 'cui-to-month', 'cui-to-day', 'cui-to-hour', 'cui-to-minute',
  ] as const) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) continue;
    el.addEventListener('change', () => {
      readCuiFromDom();
    });
  }

  app.querySelectorAll('[data-act]').forEach((btn) => {
    if ((btn as HTMLElement).tagName === 'INPUT') return;
    btn.addEventListener('click', () => {
      const act = (btn as HTMLElement).dataset.act!;
      if (act === 'cui-toggle-filters') {
        cuiFiltersExpanded = !cuiFiltersExpanded;
      } else if (act === 'cui-search') {
        runCuiyunSearch();
        return;
      } else if (act === 'cui-layer') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if (layer) {
          if (cuiLayers.has(layer)) {
            if (cuiLayers.size > 1) {
              cuiLayers.delete(layer);
              cuiLayerItems[layer].clear();
            }
          } else {
            cuiLayers.add(layer);
          }
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-item') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        const item = normalizeLayerItem((btn as HTMLElement).dataset.item ?? '');
        if (layer && item && cuiLayers.has(layer)) {
          const set = cuiLayerItems[layer];
          if (set.has(item)) set.delete(item);
          else set.add(item);
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-item-any') {
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if (layer && cuiLayers.has(layer)) {
          cuiLayerItems[layer].clear();
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-rel') {
        const raw = (btn as HTMLElement).dataset.rel ?? '';
        cuiRelation =
          raw === '生我' || raw === '我剋' || raw === '本宮' ? (raw as RelationFilter) : null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-cross-layer') {
        const side = (btn as HTMLElement).dataset.side;
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        if ((side === 'a' || side === 'b') && layer) {
          if (side === 'a') cuiCrossALayer = layer;
          else cuiCrossBLayer = layer;
        }
      } else if (act === 'cui-cross-item') {
        const side = (btn as HTMLElement).dataset.side;
        const layer = (btn as HTMLElement).dataset.layer as CuiyunLayer | undefined;
        const item = normalizeLayerItem((btn as HTMLElement).dataset.item ?? '');
        if ((side === 'a' || side === 'b') && layer && item) {
          const cur = side === 'a' ? cuiCrossA : cuiCrossB;
          const next: CrossLayerSide | null =
            cur && cur.layer === layer && cur.id === item ? null : { layer, id: item };
          if (side === 'a') {
            cuiCrossA = next;
            cuiCrossALayer = layer;
          } else {
            cuiCrossB = next;
            cuiCrossBLayer = layer;
          }
          if (cuiResult) {
            runCuiyunSearch();
            return;
          }
        }
      } else if (act === 'cui-cross-clear') {
        const side = (btn as HTMLElement).dataset.side;
        if (side === 'a') cuiCrossA = null;
        else if (side === 'b') cuiCrossB = null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-cross-rel') {
        const raw = (btn as HTMLElement).dataset.rel ?? '';
        cuiCrossRel = raw === '生' || raw === '剋' ? raw : null;
        if (cuiResult) {
          runCuiyunSearch();
          return;
        }
      } else if (act === 'cui-load') {
        const idx = Number((btn as HTMLElement).dataset.cuiIdx);
        const board = cuiResult?.boards[idx];
        if (board) {
          loadCuiyunBoard(board);
          return;
        }
      }
      render();
    });
  });

  app.querySelectorAll<HTMLInputElement>('input[data-act="cui-cross-same"]').forEach((el) => {
    el.addEventListener('change', () => {
      cuiCrossSamePalace = el.checked;
      if (cuiResult) runCuiyunSearch();
      else render();
    });
  });
}

function render(): void {
  app.innerHTML = `
    <header class="brand-header">
      <h1>Topwin 上水催運</h1>
      <p class="subtitle">奇門催運搜尋 · 日時區間 · 層門星神干 · 生我／我剋／本宮 · 跨層生剋</p>
    </header>
    ${renderCuiyunPanel()}
    ${renderChart()}
  `;
  bind();
}

render();
